import { t } from "i18next";

interface RangeDatePickerProps {
    startDate?: string | Date | null;
    endDate?: string | Date | null;
    onChange: (update: [Date | null, Date | null]) => void;
    placeholderText?: string;
    className?: string;
    withTime?: boolean;
}

export default function RangeDatePicker({
    startDate,
    endDate,
    onChange,
    withTime = false,
    className = "form-field w-full",
    placeholderText
}: RangeDatePickerProps) {
    const getValidDateStr = (date?: string | Date | null) => {
        if (!date) return "";
        const d = new Date(date as any);
        if (isNaN(d.getTime())) return "";

        const year = d.getUTCFullYear();
        const month = String(d.getUTCMonth() + 1).padStart(2, '0');
        const day = String(d.getUTCDate()).padStart(2, '0');

        if (withTime) {
            const hours = String(d.getUTCHours()).padStart(2, '0');
            const minutes = String(d.getUTCMinutes()).padStart(2, '0');
            return `${year}-${month}-${day}T${hours}:${minutes}`;
        }

        return `${year}-${month}-${day}`;
    };

    const handleStartChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        let newStart = null;
        if (val) {
            if (withTime) {
                const [datePart, timePart] = val.split("T");
                const [y, m, d] = datePart.split("-").map(Number);
                const [h, min] = (timePart || "00:00").split(":").map(Number);
                newStart = new Date(Date.UTC(y, m - 1, d, h, min, 0));
            } else {
                const [y, m, d] = val.split("-").map(Number);
                newStart = new Date(Date.UTC(y, m - 1, d, 0, 0, 0));
            }
        }
        let currentEnd = endDate ? new Date(endDate as any) : null;
        if (currentEnd && isNaN(currentEnd.getTime())) currentEnd = null;

        onChange([newStart, currentEnd]);
    };

    const handleEndChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        let newEnd = null;
        if (val) {
            if (withTime) {
                const [datePart, timePart] = val.split("T");
                const [y, m, d] = datePart.split("-").map(Number);
                const [h, min] = (timePart || "23:59").split(":").map(Number);
                newEnd = new Date(Date.UTC(y, m - 1, d, h, min, 0));
            } else {
                const [y, m, d] = val.split("-").map(Number);
                newEnd = new Date(Date.UTC(y, m - 1, d, 23, 59, 59));
            }
        }
        let currentStart = startDate ? new Date(startDate as any) : null;
        if (currentStart && isNaN(currentStart.getTime())) currentStart = null;

        onChange([currentStart, newEnd]);
    };

    const handleStartClick = (e: React.MouseEvent<HTMLInputElement>) => {
        const target = e.target as HTMLInputElement;
        if (!startDate && withTime) {
            const now = new Date();
            const newStart = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0));
            let currentEnd = endDate ? new Date(endDate as any) : null;
            if (currentEnd && isNaN(currentEnd.getTime())) currentEnd = null;
            onChange([newStart, currentEnd]);
            setTimeout(() => {
                try { if ("showPicker" in target) target.showPicker(); } catch (err) {}
            }, 0);
            return;
        }
        try { if ("showPicker" in target) target.showPicker(); } catch (err) {}
    };

    const handleEndClick = (e: React.MouseEvent<HTMLInputElement>) => {
        const target = e.target as HTMLInputElement;
        if (!endDate && withTime) {
            const baseDate = startDate ? new Date(startDate as any) : new Date();
            const y = startDate ? baseDate.getUTCFullYear() : baseDate.getFullYear();
            const m = startDate ? baseDate.getUTCMonth() : baseDate.getMonth();
            const d = startDate ? baseDate.getUTCDate() : baseDate.getDate();
            const newEnd = new Date(Date.UTC(y, m, d, 23, 59, 59));
            let currentStart = startDate ? new Date(startDate as any) : null;
            if (currentStart && isNaN(currentStart.getTime())) currentStart = null;
            onChange([currentStart, newEnd]);
            setTimeout(() => {
                try { if ("showPicker" in target) target.showPicker(); } catch (err) {}
            }, 0);
            return;
        }
        try { if ("showPicker" in target) target.showPicker(); } catch (err) {}
    };

    return (
        <div className="flex flex-row gap-4 w-full">
            <div className="flex-1">
                <label className="text-xs text-gray-500 mb-1 block capitalize">{t("common:from", "From")}</label>
                <input
                    type={withTime ? "datetime-local" : "date"}
                    value={getValidDateStr(startDate)}
                    max={endDate ? getValidDateStr(endDate) : undefined}
                    onChange={handleStartChange}
                    onClick={handleStartClick}
                    className={`${className} ${endDate && !startDate ? 'error !border-red-500' : ''}`}
                    placeholder={placeholderText}
                />
            </div>
            <div className="flex-1">
                <label className="text-xs text-gray-500 mb-1 block capitalize">{t("common:to", "To")}</label>
                <input
                    type={withTime ? "datetime-local" : "date"}
                    value={getValidDateStr(endDate)}
                    min={startDate ? getValidDateStr(startDate) : undefined}
                    onChange={handleEndChange}
                    onClick={handleEndClick}
                    className={`${className} ${startDate && !endDate ? 'error !border-red-500' : ''}`}
                    placeholder={placeholderText}
                />
            </div>
        </div>
    );
}
